/**
 * Accueil unique (refonte v4 du 17 octobre 2026) : le même écran pour tous les
 * comptes. Ses blocs se choisissent par les droits et par la vue terrain,
 * jamais par le nom du rôle. De haut en bas :
 *
 * 1. l'en-tête : bonjour, la date, la journée en une phrase (astreinte en
 *    cours comprise : « d'astreinte jusqu'au lun. 26 oct. 08:00 ») ;
 * 2. « Maintenant » : l'intervention en cours ou la suivante (terrain), ce qui
 *    réclame le bureau tout de suite, ou un état calme. La personne d'astreinte
 *    voit au-dessus l'urgence à prendre en charge, « Prendre en charge » en
 *    action principale ; qui ne lit pas le planning (comptabilité) n'a jamais
 *    une urgence frigorifique en tête, seulement une ligne d'information ;
 * 3. « À traiter » : une seule file, du plus urgent au moins urgent, chaque
 *    ligne ouvrant l'enregistrement exact (`queue.ts`) ;
 * 4. « Aujourd'hui » : l'équipe du jour (qui voit tout le planning) ou sa
 *    propre journée, interventions puis tâches (terrain) ;
 * 5. « Repères » : les chiffres clés permis par les droits, repliés au téléphone ;
 * 6. un seul conseil de prise en main, fermable : la « Mise en route » de
 *    l'entreprise (une carte de progression vers sa page) pour qui la règle, sinon « Bien démarrer », sinon le conseil de la vue.
 *
 * Toutes les requêtes partent en même temps ; chaque bloc montre son propre
 * chargement (symbole MyEquip), son erreur expliquée ou son contenu.
 */
import { useCallback, useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, MapPin, Phone, Wrench } from 'lucide-react';
import { approxClock } from '@myequip/shared';
import { api, ApiError } from '../../core/api.js';
import { isFieldWorker, useAuth, useCan, useTerrainView } from '../../core/auth.js';
import { dateLongue } from '../../core/format.js';
import { t } from '../../core/i18n.js';
import { callablePhone, googleMapsDirectionsUrl, wazeDirectionsUrl } from '../../core/navigation.js';
import { CallButton } from '../../components/CallButton.js';
import { HubPage } from '../../components/templates/templates.js';
import { ConfirmActionModal, StateBadge } from '../../components/ui.js';
import { Banner, resultTone } from '../../components/ui/Feedback.js';
import { PasskeyOffer, usePasskeyOffer } from '../profile/PasskeyOffer.js';
import { DetailLayout } from '../../components/ui/DetailLayout.js';
import { NowCard } from '../../components/ui/NowCard.js';
import type { Action } from '../../components/ui/action.js';
import { PageHelpButton, useOnboardingTip } from '../../components/shell-extras.js';
import { useCompanyDocuments } from '../company-documents/company-documents.js';
import { useHomeAnnouncements } from '../messages/messages-data.js';
import { StartChecklist, useOnboarding } from '../onboarding/Welcome.js';
import { CompanySetupCard, useCompanySetup, type CompanySetup } from '../onboarding/CompanySetupCard.js';
import {
  ABSENCE_DECISION_QUERY_KEYS, absencePeriod, absenceRepeats, usePendingAbsenceRequests, type AbsenceRequest,
} from '../planning/absence-requests.js';
import { usePlanningSeen } from '../planning/seen.js';
import { useOpenTasks } from '../tasks/TasksPage.js';
import { useEmergencyAcks } from '../emergencies/emergency-ack.js';
import {
  OFFICE_HOME_CAPABILITIES, useExceptions, useMyDay, useObjectivesToday, useReminders, useReperes, useTeamToday,
  type DayIntervention, type MyDay,
} from './home-data.js';
import { HomeQueue, QueueIcon, type QueueFeedback } from './HomeQueue.js';
import { HomeReperes } from './HomeReperes.js';
import { localDay } from './local-day.js';
import { interventionBadge, MyDayList } from './MyDayList.js';
import { buildQueue, pickNow, withoutNow, type QueueItem } from './queue.js';
import { TodayTeam } from './TodayTeam.js';
import { useOncallNow, whenShort } from '../team/oncall-model.js';
import { canReadPlanning } from '../interventions/list/tabs.js';

const ACTIVE = new Set(['en_route', 'en_cours', 'en_pause']);
const ACTIVE_RANK: Record<string, number> = { en_cours: 0, en_pause: 1, en_route: 2 };
/** Lignes de « À traiter » montrées d'emblée (les urgences critiques le sont toujours). */
const QUEUE_LIMIT = 5;

const typeLabel = (type: string): string => t(`interventions.types.${type}`);

function capitalized(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** Rappels reportés à demain sur cet appareil (jour de l'appareil, jamais UTC), propres au compte. */
const LATER_PREFIX = 'myequip.reminders.';

function laterKey(userId: string | undefined, day: string): string {
  return `${LATER_PREFIX}${userId ?? 'anonyme'}.${day}`;
}

function readLater(key: string): string[] {
  try {
    const stored: unknown = JSON.parse(localStorage.getItem(key) ?? '[]');
    return Array.isArray(stored) ? stored.filter((value): value is string => typeof value === 'string').slice(0, 50) : [];
  } catch {
    return [];
  }
}

function writeLater(key: string, ids: string[]): void {
  try {
    // Un seul jour gardé par compte : les reports des jours passés n'ont plus d'effet.
    const account = key.slice(0, key.lastIndexOf('.') + 1);
    for (let index = localStorage.length - 1; index >= 0; index -= 1) {
      const stored = localStorage.key(index);
      if (stored && stored !== key && stored.startsWith(account)) localStorage.removeItem(stored);
    }
    if (ids.length === 0) localStorage.removeItem(key);
    else localStorage.setItem(key, JSON.stringify(ids.slice(-50)));
  } catch {
    // Stockage indisponible : le report vaut pour cet écran.
  }
}

/** La visite de la carte « Maintenant » au terrain : celle en cours, sinon la prochaine prévue. */
export function fieldNow(day: MyDay | undefined): { intervention: DayIntervention; active: boolean } | undefined {
  const visits = day?.interventions ?? [];
  // Plusieurs visites ouvertes (un trajet oublié, puis le travail du moment) : le travail
  // l'emporte sur la pause, la pause sur le trajet, puis la plus récente (comme l'équipe du jour).
  const active = visits
    .filter((visit) => ACTIVE.has(visit.status))
    .sort((a, b) => (ACTIVE_RANK[a.status] ?? 9) - (ACTIVE_RANK[b.status] ?? 9) || b.starts_at.localeCompare(a.starts_at))[0];
  if (active) return { intervention: active, active: true };
  const next = visits.find((visit) => visit.status === 'planifiee');
  return next ? { intervention: next, active: false } : undefined;
}

function primaryFor(item: QueueItem): string {
  switch (item.kind) {
    case 'demande': return 'Qualifier la demande';
    case 'retard':
    case 'oubli':
    case 'non_affectee': return 'Ouvrir l’intervention';
    case 'sla': return 'Ouvrir';
    default: return 'Ouvrir';
  }
}

/** Marque le planning comme vu à l'ouverture de sa journée (terrain). */
function MarkPlanningSeen(): null {
  usePlanningSeen();
  return null;
}

/**
 * Une entreprise qui démarre : son activité n'est pas encore choisie. Sa
 * « Mise en route » passe alors en tête de l'accueil, avant « À traiter » ;
 * ensuite, le travail du jour reste en tête et la carte descend en bas.
 */
function startingCompany(setup: CompanySetup | undefined): boolean {
  return Boolean(setup?.visible && setup.items.some((item) => item.key === 'metiers' && !item.done));
}

/** La « Mise en route » d'une entreprise qui démarre (qui règle l'entreprise, au bureau), en tête. */
function HomeSetup({ field }: { field: boolean }): ReactNode {
  const can = useCan();
  const onboarding = useOnboarding();
  const companySetup = !field && can('settings.manage');
  const setup = useCompanySetup(companySetup);
  if (onboarding.data?.welcome || !companySetup || !startingCompany(setup.data)) return null;
  return <CompanySetupCard />;
}

/**
 * Un seul conseil, fermable, en bas de l'accueil : la « Mise en route » tant
 * qu'elle sert (rien ici quand elle est en tête), sinon « Bien démarrer »,
 * sinon Face ID à activer tant que le compte n'a pas de clé d'accès (issue
 * #138), sinon le conseil de la vue.
 */
function HomeTip({ field }: { field: boolean }): ReactNode {
  const can = useCan();
  const onboarding = useOnboarding();
  const companySetup = !field && can('settings.manage');
  const setup = useCompanySetup(companySetup);
  const tip = useOnboardingTip(field ? 'technicien_journee' : 'patron_accueil');
  const passkeyOffer = usePasskeyOffer();
  if (onboarding.data?.welcome) return null;
  if (companySetup && startingCompany(setup.data)) return null;
  if (companySetup && setup.data?.visible) return <CompanySetupCard />;
  if (onboarding.data?.checklist) return <StartChecklist />;
  // Se connecter d'un appui passe avant le conseil de la vue (issue #138).
  if (passkeyOffer.visible) return <PasskeyOffer onDone={passkeyOffer.hide} onAdded={passkeyOffer.added} />;
  if (!tip.visible || !onboarding.settled || (companySetup && setup.isLoading)) return null;
  return (
    <Banner tone="info" onDismiss={() => void tip.dismiss()}>
      {field ? t('aide.technicienJournee') : t('aide.patronAccueil')}
    </Banner>
  );
}

export function HomePage(): ReactNode {
  const { me } = useAuth();
  const can = useCan();
  const field = useTerrainView();
  const queryClient = useQueryClient();
  const now = new Date();
  const today = localDay(now);
  const office = !field;
  // Les vues d'ensemble du bureau, seulement pour qui détient un droit qui les ouvre (sinon 403).
  const officeData = office && OFFICE_HOME_CAPABILITIES.some((capability) => can(capability));
  const seesTeam = office && can('planning.view_all');

  // Toutes les lectures partent en même temps, chacune derrière son droit.
  const exceptions = useExceptions(officeData);
  const myDay = useMyDay(me?.id, field);
  const reminders = useReminders(me?.id);
  const team = useTeamToday(seesTeam);
  const reperes = useReperes(officeData);
  const absences = usePendingAbsenceRequests(office && can('absence.approve'));
  const objectives = useObjectivesToday(office && can('task.assign'));
  const documents = useCompanyDocuments();
  const announcements = useHomeAnnouncements(can('messaging.use'));
  const tasks = useOpenTasks();
  const oncall = useOncallNow();

  // Rappels reportés à demain, sur cet appareil.
  const laterStorage = laterKey(me?.id, today);
  const [later, setLater] = useState<string[]>(() => readLater(laterStorage));
  const [feedback, setFeedback] = useState<QueueFeedback[]>([]);
  const say = useCallback((entry: QueueFeedback) => {
    setFeedback((current) => [entry, ...current.filter((item) => item.key !== entry.key)].slice(0, 3));
  }, []);
  const closeFeedback = useCallback((key: string) => {
    setFeedback((current) => current.filter((item) => item.key !== key));
  }, []);

  // Prise en charge d'une urgence : confirmée avant l'envoi, gardée sur l'appareil hors ligne.
  const refreshAlerts = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['dashboard-exceptions'] });
    void queryClient.invalidateQueries({ queryKey: ['my-day'] });
  }, [queryClient]);
  const ack = useEmergencyAcks(me?.id, refreshAlerts);
  const [confirmAck, setConfirmAck] = useState<{ alertId: string; title: string }>();

  // Absences : accepter d'un geste (décision du 15/10/2026), refuser avec un motif.
  const [refusing, setRefusing] = useState<AbsenceRequest>();
  const decide = useMutation({
    mutationFn: (value: { request: AbsenceRequest; decision: 'approuve' | 'refuse'; reason?: string }) =>
      api(`/schedule-events/${value.request.id}/approve`, {
        method: 'POST', body: { decision: value.decision, ...(value.reason ? { reason: value.reason } : {}) },
      }),
    onSuccess: async (_result, value) => {
      setRefusing(undefined);
      const name = value.request.user_name ?? '';
      say({
        key: `absence:${value.request.id}`,
        state: 'confirmed',
        text: value.decision === 'approuve'
          ? `Absence de ${name} acceptée : la personne est prévenue et son planning est bloqué.`
          : `Absence de ${name} refusée : la personne est prévenue, avec le motif.`,
      });
      await Promise.all(ABSENCE_DECISION_QUERY_KEYS.map((queryKey) =>
        queryClient.invalidateQueries({ queryKey: [...queryKey] })));
    },
    onError: (error, value) => {
      if (value.decision === 'refuse') return; // l'erreur reste dans la fenêtre de refus
      say({
        key: `absence:${value.request.id}`,
        state: 'failed',
        text: error instanceof ApiError && error.code === 'network_error'
          ? 'Hors ligne : la décision n’est pas partie. Réessayez au retour du réseau.'
          : `La décision n’a pas pu être enregistrée : ${(error as Error).message}`,
      });
    },
  });
  const pendingAbsences = absences.data ?? [];

  // Qui ne lit pas le planning (comptabilité) ne reçoit pas les urgences frigorifiques en tête de
  // son accueil : une ligne d'information suffit, l'équipe terrain et le bureau s'en occupent.
  const readsPlanning = canReadPlanning(can);
  const hiddenUrgences = office && !readsPlanning ? exceptions.data?.urgences.length ?? 0 : 0;
  const shownExceptions = exceptions.data && hiddenUrgences > 0 ? { ...exceptions.data, urgences: [] } : exceptions.data;

  const queue = buildQueue({
    field,
    exceptions: shownExceptions,
    myDay: myDay.data,
    reminders: reminders.data,
    hiddenReminders: later,
    absences: pendingAbsences,
    absenceLabel: (absence) => {
      const kind = t(`planning.evenements.${absence.kind}`);
      // Le titre donné par la personne, quand il dit plus que le type (« École », « Mariage »).
      const title = (absence as AbsenceRequest).title;
      return [kind, absencePeriod(absence), absenceRepeats(absence as AbsenceRequest), title && title !== kind ? title : '']
        .filter(Boolean).join(' · ');
    },
    documents: documents.data?.items,
    announcements: announcements.data?.pinned,
    tasks: office ? tasks.items : undefined,
    objectives: objectives.data?.people,
    typeLabel,
  }, now);

  const officeNow = office ? pickNow(queue) : undefined;
  const fieldVisit = field ? fieldNow(myDay.data) : undefined;
  // Personne d'astreinte : l'urgence à prendre en charge passe au-dessus de « Maintenant ».
  const oncallUrgency = field && oncall.data?.mine.current
    ? queue.find((item) => item.kind === 'urgence' && item.act?.kind === 'prise_en_charge')
    : undefined;
  const featured = officeNow ?? oncallUrgency;
  // Ce que « Maintenant » montre déjà ne se répète pas dans la file (`withoutNow`).
  const rest = withoutNow(queue, {
    key: featured?.key,
    visit: fieldVisit ? `/interventions/${fieldVisit.intervention.id}` : undefined,
  });

  const queueSource = field ? myDay : exceptions;
  // La file ne s'affiche qu'une fois ses sources lues : sinon une ligne déjà montrée
  // glisserait sous « Voir les N autres » à l'arrivée d'une plus urgente.
  const queueSettling = [queueSource, reminders, absences, objectives, documents, announcements]
    .some((query) => query.isLoading && !query.data) || (office && tasks.loading);

  if (!me) return null;
  const firstName = me.full_name.trim().split(/\s+/)[0] ?? '';

  // La journée en une phrase.
  const summary: string[] = [];
  if (field) {
    // Rien n'est affirmé avant que la journée soit lue (réseau ou appareil) : ni pendant le
    // chargement, ni après un échec, l'en-tête ne dit « Aucune intervention aujourd'hui ».
    if (myDay.data) {
      const visits = myDay.data.interventions;
      const active = visits.filter((visit) => ACTIVE.has(visit.status)).length;
      summary.push(visits.length === 0
        ? t('accueil.aucuneInterventionAujourdhui')
        : `${visits.length} intervention${visits.length > 1 ? 's' : ''} aujourd’hui${active ? `, dont ${active} en cours` : ''}`);
    }
    // Un responsable qui intervient aussi : sa vue terrain ne montre que ses missions.
    if (me.field_operator && !isFieldWorker(me)) summary.push('vos propres missions');
  } else {
    if (team.data) {
      const { interventions, active } = team.data.totals;
      summary.push(interventions === 0
        ? t('accueil.aucuneInterventionAujourdhui')
        : `${interventions} intervention${interventions > 1 ? 's' : ''} aujourd’hui${active ? `, dont ${active} en cours` : ''}`);
    }
    // Le compte n'est dit qu'une fois la file lue, alertes comprises (pas pendant le chargement
    // ni après un échec) ; il compte aussi la ligne mise en avant dans « Maintenant ».
    if (!queueSettling && (!officeData || exceptions.data)) {
      summary.push(queue.length === 0 ? 'rien à traiter' : `${queue.length} à traiter`);
    }
  }
  // Astreinte en cours de la personne (19/10/2026) : dite dans la phrase du jour, sans bloc de plus.
  const myOncall = oncall.data?.mine.current;
  if (myOncall) summary.push(`d’astreinte jusqu’au ${whenShort(myOncall.ends_at)}`);
  const description = capitalized(summary.join(' · '));

  // Une ligne de la file en grande carte : « Maintenant » au bureau, l'urgence d'astreinte au terrain.
  const queueCard = (item: QueueItem): ReactNode => {
    const act = item.act?.kind === 'prise_en_charge' ? item.act : undefined;
    const ackState = act ? ack.states[act.alertId] : undefined;
    // Prise en charge : en attente pendant l'envoi, retirée une fois en file ou confirmée, de retour si elle échoue.
    const primary: Action | undefined = act
      ? ackState && (ackState.status === 'queued' || ackState.status === 'confirmed')
        ? undefined
        : {
            label: t('accueil.prendreEnCharge'),
            pending: ackState?.status === 'pending',
            pendingLabel: 'Prise en charge…',
            onClick: () => setConfirmAck({ alertId: act.alertId, title: item.detail }),
          }
      : { label: primaryFor(item), to: item.to, state: { from: '/' } };
    return (
      <NowCard
        heading={t('accueil.aTraiterEnPremier')}
        tone={item.severity === 'critique' ? 'urgent' : 'default'}
        icon={<QueueIcon kind={item.kind} size={16} />}
        title={item.title}
        lines={[item.detail, item.sinceLabel]}
        {...(primary ? { primary } : {})}
        {...(act ? { secondary: { label: 'Détails', to: item.to, state: { from: '/' } } } : {})}
      >
        {act && ackState && ackState.status !== 'pending' ? (
          <Banner tone={resultTone(ackState.status)} onDismiss={() => ack.dismiss(act.alertId)}>{ackState.message}</Banner>
        ) : null}
      </NowCard>
    );
  };

  const nowCard = (() => {
    if (field) {
      // Pendant le chargement ou après un échec, rien n'est affirmé : « À traiter » dit l'état.
      if (!myDay.data) return null;
      if (!fieldVisit) {
        // Plus rien d'ouvert ni de prévu : une journée vide, ou une journée finie (jamais
        // « Aucune intervention » quand l'en-tête en compte de terminées).
        const finished = myDay.data.interventions.length > 0;
        return (
          <NowCard heading={t('accueil.maintenant')} tone="calm" icon={<CheckCircle2 size={16} />}
            title={finished ? t('accueil.journeeTerminee') : t('accueil.aucuneInterventionAujourdhui')}
            lines={[finished ? 'Toutes vos interventions du jour sont terminées.' : t('accueil.journeeVideRassurante')]}
            primary={{ label: 'Voir mon planning', to: '/planning' }} demoted={Boolean(oncallUrgency)} />
        );
      }
      const { intervention, active } = fieldVisit;
      const destination = { address: intervention.site_address, latitude: intervention.latitude ?? null, longitude: intervention.longitude ?? null };
      return (
        <NowCard
          heading={active ? t('accueil.maintenant') : t('accueil.ensuite')}
          icon={<Wrench size={16} />}
          title={`${approxClock(intervention.starts_at, intervention.time_approx_minutes)} · ${typeLabel(intervention.type)}`}
          lines={[
            [intervention.customer_name, intervention.site_label].filter(Boolean).join(' · '),
            intervention.site_address,
          ]}
          badge={<StateBadge state={interventionBadge(intervention)} />}
          primary={{ label: active ? t('accueil.continuer') : t('commun.ouvrir'), to: `/interventions/${intervention.id}`, state: { from: '/' } }}
          demoted={Boolean(oncallUrgency)}
          {...(intervention.site_address
            ? {
                secondary: { label: 'Itinéraire', href: googleMapsDirectionsUrl(destination), icon: <MapPin size={18} aria-hidden="true" /> },
                menu: [{ label: 'Itinéraire avec Waze', onSelect: () => { window.open(wazeDirectionsUrl(destination), '_blank', 'noopener'); } }],
              }
            : {})}
          extra={callablePhone(intervention) ? (
            <CallButton phone={callablePhone(intervention)!} who={intervention.customer_name}>
              <Phone size={18} aria-hidden="true" /> Appeler
            </CallButton>
          ) : undefined}
        />
      );
    }
    // Pendant le chargement ou après un échec, rien n'est affirmé : « À traiter » dit l'état.
    if (officeData && !exceptions.data) return null;
    if (!officeNow) {
      return (
        <NowCard heading={t('accueil.maintenant')} tone="calm" icon={<CheckCircle2 size={16} />}
          title={t('accueil.rienDUrgent')}
          lines={[rest.length > 0 ? t('accueil.resteSansUrgence', { n: rest.length }) : t('accueil.toutVaBien')]}
          primary={{ label: t('accueil.voirLePlanning'), to: '/planning' }} />
      );
    }
    return queueCard(officeNow);
  })();

  // Retours des prises en charge faites depuis la file (la carte « Maintenant » affiche les siens).
  const ackFeedback: QueueFeedback[] = Object.entries(ack.states)
    .filter(([alertId, state]) => state.status !== 'pending' && !(featured?.act?.kind === 'prise_en_charge' && featured.act.alertId === alertId))
    .map(([alertId, state]) => ({ key: `urgence:${alertId}`, state: state.status as QueueFeedback['state'], text: state.message ?? '' }));

  // Sur grand écran, l'équipe du jour et les repères du bureau passent à droite de la file
  // (une colonne au téléphone) ; au terrain, la journée reste dans la colonne principale.
  const reperesShown = Boolean(reperes.isLoading || reperes.error
    || reperes.data?.impayes || reperes.data?.devisEnAttente || reperes.data?.chiffreAffairesMois);
  const hasAside = office && (seesTeam || (officeData && reperesShown));

  const queueBlock = (
    <HomeQueue
      items={rest}
      loading={queueSettling}
      // Une lecture qui échoue sans rien en mémoire se dit, même si d'autres lignes (tâches, rappels) restent.
      error={queueSource.data ? undefined : queueSource.error}
      onRetry={() => void queueSource.refetch()}
      limit={QUEUE_LIMIT}
      feedback={[...ackFeedback, ...feedback]}
      onCloseFeedback={(key) => {
        if (key.startsWith('urgence:')) ack.dismiss(key.slice('urgence:'.length));
        closeFeedback(key);
      }}
      actions={{
        ackStates: ack.states,
        onAck: (alertId, title) => setConfirmAck({ alertId, title }),
        decidingAbsence: decide.isPending ? decide.variables?.request.id : undefined,
        onAccept: (requestId) => {
          const request = pendingAbsences.find((item) => item.id === requestId);
          if (request) {
            closeFeedback(`absence:${requestId}`);
            decide.mutate({ request, decision: 'approuve' });
          }
        },
        onRefuse: (requestId) => {
          decide.reset();
          setRefusing(pendingAbsences.find((item) => item.id === requestId));
        },
        onLater: (reminderId) => {
          const next = [...later, reminderId];
          setLater(next);
          writeLater(laterStorage, next);
          say({
            key: `rappel:${reminderId}`,
            state: 'confirmed',
            text: 'Rappel reporté à demain.',
            undo: () => {
              setLater((current) => {
                const restored = current.filter((id) => id !== reminderId);
                writeLater(laterStorage, restored);
                return restored;
              });
              closeFeedback(`rappel:${reminderId}`);
            },
          });
        },
      }}
    />
  );
  const dayBlock = field && myDay.data ? (
    <MyDayList day={myDay.data} nowId={fieldVisit?.intervention.id} tasks={tasks.items} taskCount={tasks.count} />
  ) : null;
  const asideBlock = hasAside ? (
    <>
      {seesTeam ? <TodayTeam query={team} objectives={objectives.data?.people ?? []} day={today} /> : null}
      {officeData ? <HomeReperes reperes={reperes} team={team.data} /> : null}
    </>
  ) : undefined;

  return (
    <HubPage header={{
      // Le titre nomme l'écran (barre du haut, retour) ; le bonjour et la date le situent.
      kicker: `${firstName ? `Bonjour ${firstName} · ` : ''}${dateLongue(now)}`,
      title: field ? t('nav.maJournee') : t('accueil.tableauDeBord'),
      description,
      help: (
        <PageHelpButton title="Comprendre l’accueil">
          <p>
            « Maintenant » montre la seule chose qui compte à cet instant. « À traiter » rassemble tout ce
            qui vous attend, du plus urgent au moins urgent : chaque ligne ouvre le dossier concerné.
            « Aujourd’hui » dit {field ? 'la suite de votre journée, puis vos tâches' : 'qui fait quoi, où, et qui est absent'}.
            {field ? '' : ' Les chiffres de l’entreprise sur une période se lisent dans Analyses.'}
          </p>
        </PageHelpButton>
      ),
    }}>
      {field ? <MarkPlanningSeen /> : null}
      {field && myDay.data?.offline ? (
        <Banner tone="warn" title="Hors ligne">
          Votre journée vient de cet appareil ; elle se met à jour au retour du réseau.
        </Banner>
      ) : null}
      {hiddenUrgences > 0 ? (
        <Banner tone="info">
          {hiddenUrgences > 1
            ? t('accueil.urgencesInfoPluriel', { n: hiddenUrgences })
            : t('accueil.urgencesInfo')}
        </Banner>
      ) : null}
      <HomeSetup field={field} />
      <DetailLayout asideLabel={t('accueil.aujourdhui')} main={<>{oncallUrgency ? queueCard(oncallUrgency) : null}{nowCard}{queueBlock}{dayBlock}</>} aside={asideBlock} />
      <HomeTip field={field} />

      <ConfirmActionModal
        open={confirmAck !== undefined}
        title="Prendre en charge cette urgence ?"
        consequence={confirmAck
          ? `« ${confirmAck.title} » : l’escalade s’arrête et l’équipe voit que vous vous en occupez.`
          : ''}
        confirmLabel={t('accueil.prendreEnCharge')}
        pending={confirmAck ? ack.states[confirmAck.alertId]?.status === 'pending' : false}
        onClose={() => setConfirmAck(undefined)}
        onConfirm={() => {
          if (!confirmAck) return;
          const { alertId } = confirmAck;
          setConfirmAck(undefined);
          void ack.acknowledge(alertId);
        }}
      />
      <ConfirmActionModal
        open={refusing !== undefined}
        title="Refuser cette absence ?"
        consequence={refusing
          ? `${absencePeriod(refusing)} : la personne est prévenue avec le motif, son planning reste libre.`
          : ''}
        confirmLabel="Refuser l’absence"
        tone="danger"
        pending={decide.isPending}
        error={decide.error && refusing ? (decide.error as Error).message : undefined}
        reason={{ label: 'Motif du refus', required: true, minLength: 3, placeholder: 'Ex. trop de monde absent cette semaine-là' }}
        onClose={() => setRefusing(undefined)}
        onConfirm={(reason) => {
          if (refusing) decide.mutate({ request: refusing, decision: 'refuse', ...(reason ? { reason } : {}) });
        }}
      />
    </HubPage>
  );
}
